/**
 * UpcomingMilestones — CCD-DASH-014 Phase A · R3b.
 *
 * A chronological look-ahead of the next real dates on the traveler's
 * horizon (Final Payment, Check-in Opens, Boarding Time, Embarkation).
 * Reads from the composed `today.planning_block.milestones[]` payload
 * — the milestones composer is already the authoritative source.
 *
 * Design: /app/docs/design/CCD-DASH-014-dashboard-design-sprint.md §1.1 · R3b
 */
import React from "react";
import { useCruise } from "@/context/CruiseContext";
import { CalendarClock } from "lucide-react";
import HomeSummaryCard from "./HomeSummaryCard";

const SEVERITY_TONE = {
  critical:      { dot: "bg-rose-500",    label: "text-rose-700" },
  upcoming:      { dot: "bg-amber-500",   label: "text-amber-700" },
  informational: { dot: "bg-slate-400",   label: "text-slate-500" },
};

function _humanDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  } catch { return iso; }
}

function _daysUntil(iso) {
  if (!iso) return null;
  try {
    const then = new Date(`${iso}T00:00:00Z`).getTime();
    const now  = new Date();
    const utcMidnightNow = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const days = Math.round((then - utcMidnightNow) / 86400000);
    return days;
  } catch { return null; }
}

export function UpcomingMilestones() {
  const { today, setRoute } = useCruise();
  const milestones = today?.planning_block?.milestones || [];
  // Only forward-looking milestones, chronological, max 6.
  const rows = milestones
    .filter((m) => (m?.date || m?.at_iso))
    .slice(0, 6);

  return (
    <HomeSummaryCard
      eyebrow="Upcoming Milestones"
      icon={<CalendarClock className="w-3 h-3" />}
      title="What's coming"
      testId="upcoming-milestones-card"
    >
      {rows.length === 0 ? (
        <div className="font-body text-[13px] text-slate-500 leading-relaxed">
          No dated milestones on your horizon yet.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="upcoming-milestones-list">
          {rows.map((m, i) => {
            const iso = m.date || (m.at_iso ? m.at_iso.slice(0, 10) : null);
            const days = _daysUntil(iso);
            const tone = SEVERITY_TONE[m.severity] || SEVERITY_TONE.informational;
            const key = m.id || `${m.title}-${i}`;
            return (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => m.route && setRoute(m.route)}
                  className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-slate-50 transition-colors -mx-1 px-1 rounded"
                  data-testid={`upcoming-milestone-${m.id || i}`}
                >
                  <span className={`mt-0.5 w-1.5 h-1.5 rounded-full shrink-0 ${tone.dot}`} aria-hidden="true" />
                  <span className="flex-1 min-w-0">
                    <span className="block font-body text-[13px] text-slate-800 truncate">
                      {m.title || m.label}
                    </span>
                    <span className="block font-body text-[11px] text-slate-500">
                      {_humanDate(iso)}
                    </span>
                  </span>
                  {days !== null && days >= 0 && (
                    <span className="font-body text-[11px] font-semibold text-slate-500 tabular-nums">
                      {days === 0 ? "today" : `${days} day${days === 1 ? "" : "s"}`}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </HomeSummaryCard>
  );
}

export default UpcomingMilestones;
